import { isPlatformBrowser } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import * as Rx from 'rxjs';
import {
  map,
  mapTo,
  skip,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AUTH_CONFIG } from './auth.config';
import { AuthConfig } from './auth.interface';

export interface AuthState {
  userId: string;
  token: string;
}

export interface SwitchAccountResponse {
  status: string;
  result: {
    token: string;
    user: number;
  };
}

export interface NavigationSwitchAccountResponse {
  status: string;
  result: {
    gotoUrl?: string;
  };
}

@Injectable()
export class Auth {
  private _authStateSubject$: Rx.ReplaySubject<AuthState | undefined>;

  get authState$(): Rx.Observable<AuthState | undefined> {
    return this._authStateSubject$.asObservable();
  }

  // TODO: we should provide those to CookieModule.forRoot but it's tricky
  // because of AoT, best is probably to extend `@laurentgoudet/ngx-cookie`
  private authCookiesExpires = new Date(
    new Date().setMonth(new Date().getMonth() + 1),
  );

  constructor(
    @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
    @Inject(PLATFORM_ID) platformId: Object,
    private cookies: CookieService,
    private http: HttpClient,
  ) {
    this._authStateSubject$ = new Rx.ReplaySubject(1);
    // read session from cookie if any
    let userId;
    let token;
    if (isPlatformBrowser(platformId)) {
      userId = this.cookies.get(this.authConfig.userIdCookie);
      token = this.cookies.get(this.authConfig.authHashCookie);
    }
    if (userId && token) {
      this._authStateSubject$.next({ userId, token });
    } else {
      this._authStateSubject$.next(undefined);
    }
    // keep auth cookies in sync with session
    this._authStateSubject$.pipe(skip(1)).subscribe(s => {
      if (s) {
        this.cookies.put(this.authConfig.userIdCookie, s.userId, {
          expires: this.authCookiesExpires,
        });
        this.cookies.put(this.authConfig.authHashCookie, s.token, {
          expires: this.authCookiesExpires,
        });
      } else {
        this.cookies.remove(this.authConfig.userIdCookie);
        this.cookies.remove(this.authConfig.authHashCookie);
      }
    });
  }

  getUserId(): Rx.Observable<string> {
    return this._authStateSubject$
      .asObservable()
      .pipe(switchMap(auth => (auth ? Rx.of(auth.userId) : Rx.NEVER)));
  }

  private generateCSRFToken(length: number, charset: string): string {
    let result = '';
    for (let i = length; i > 0; --i) {
      result += charset[Math.floor(Math.random() * charset.length)];
    }
    return result;
  }

  getCSRFToken(): string {
    let token = this.cookies.get(this.authConfig.csrfCookie);
    if (!token) {
      token = this.generateCSRFToken(
        64,
        '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
      );
      this.cookies.put(this.authConfig.csrfCookie, token, {
        expires: this.authCookiesExpires,
      });
    }
    return token;
  }

  getAuthorizationHeader(): Rx.Observable<HttpHeaders> {
    return this.authState$.pipe(
      map(auth => {
        if (!auth) {
          return new HttpHeaders();
        }
        return new HttpHeaders().set(
          'freelancer-auth-v2',
          `${auth.userId};${auth.token}`,
        );
      }),
    );
  }

  setSession(userId: string, token: string) {
    this._authStateSubject$.next({ userId, token });
  }

  deleteSession() {
    this._authStateSubject$.next(undefined);
  }

  // FIXME T38832: This needs 2 AJAX calls because GAF.
  switchUser(newUserId: string): Promise<string | undefined> {
    return this.authState$
      .pipe(
        take(1),
        switchMap(auth => {
          if (!auth) {
            throw new Error('no user to switch account from');
          }
          const body = new FormData();
          body.append('user', auth.userId);
          body.append('other_user', newUserId);
          body.append('token', auth.token);

          return this.http
            .post<SwitchAccountResponse>(
              `${this.authConfig.baseUrl}/switch_account/`,
              body,
            )
            .pipe(map(response => response.result.token));
        }),
        withLatestFrom(this.getAuthorizationHeader()),
        switchMap(([newUserToken, authHeader]) => {
          const body = new FormData();
          body.append('user', newUserId);
          body.append('token', newUserToken);
          return this.http
            .post<NavigationSwitchAccountResponse>(
              this.authConfig.switchAccountHackyEndpoint,
              body,
              {
                headers: authHeader,
              },
            )
            .pipe(map(response => ({ response, newUserToken })));
        }),
        tap(({ newUserToken }) =>
          this._authStateSubject$.next({
            userId: newUserId,
            token: newUserToken,
          }),
        ),
        map(({ response }) => response.result.gotoUrl),
      )
      .toPromise();
  }

  logout(): Promise<undefined> {
    return this.authState$
      .pipe(
        take(1),
        switchMap(auth => {
          if (!auth) {
            throw new Error('no user to logout');
          }
          const body = new FormData();
          body.append('user', auth.userId);
          body.append('token', auth.token);
          return this.http.post(`${this.authConfig.baseUrl}/logout/`, body);
        }),
        tap(() => this._authStateSubject$.next(undefined)),
        mapTo(undefined),
      )
      .toPromise()
      .catch((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred.
          throw err.error;
        } else {
          // The backend returned an unsuccessful response code.
          throw new Error(err.message);
        }
      });
  }
}
